import React, { useCallback, useEffect, useState } from "react";
import "./style.scss";
import { Input, message, Spin } from "antd";
import Button from "../../../common/Button";
import { useDropzone } from "react-dropzone";
import uploadImage from "../../../assets/CreateCategory/upload.svg";
import CustomDropZone from "../../../common/CustomDropZone";
import dividerImage from "../../../assets/CreateCategory/divider.svg";
import { useCreateNewMarketPlace } from "../../../hooks/useGuidelines";
import guidelinesApi from "../../../apis/guidelines";

const CreateMarketplace = ({
  isMarketPlaceCreated,
  setIsCreateMarketplace,
}) => {
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const { getMarketPlacePresignedUrl, getThumbPathOfImage } = guidelinesApi;
  const [fileUpload, setFileUpload] = useState({});
  const [isSuccess, setIsSuccess] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [isImageLoading, setIsImageLoading] = useState(true);

  const onSuccessCreateMarketPlace = (res) => {
    if (res.status === 200) {
      message.success("Form submitted successfully.");
      setTimeout(() => {
        isMarketPlaceCreated();
      });
    }
  };

  const onErrorCreateMarketPlace = (err) => {
    message.error(err);
  };

  const { mutate: createNewMarketPlace } = useCreateNewMarketPlace(
    onSuccessCreateMarketPlace,
    onErrorCreateMarketPlace
  );

  const handleImageLoad = () => {
    setIsImageLoading(false);
  };

  const [formInput, setFormInput] = useState({
    marketPlaceName: "",
    marketPlacelogo: "",
    reason: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const allowedExtensions = ["jpg", "jpeg", "png", "webp"];

  const validateForm = () => {
    const newErrors = {};
    if (!formInput.marketPlaceName.trim()) {
      newErrors.marketPlaceName = "MarketPlace name is required.";
    }

    if (!formInput.marketPlacelogo) {
      newErrors.marketPlacelogo = "MarketPlace Logo is required.";
    }

    if (!formInput.reason) {
      newErrors.reason = "Reason is required.";
    }

    setErrors(newErrors);
    setIsSubmitted(false);
    return Object.keys(newErrors).length === 0;
  };

  const getKeyPresignedUrl = async (file) => {
    let uploadSuccess = true;
    setIsSuccess(true);
    try {
      if (!file[0].name || !file[0].type) {
        uploadSuccess = false;
        setIsSuccess(false);
        throw new Error(
          "Invalid file detected. Ensure the file has a valid name and type."
        );
      }

      const resp = await getMarketPlacePresignedUrl({
        filename: file[0].name,
        mimeType: file[0].type,
      });

      if (resp?.status !== 200 || !resp?.data?.key || !resp?.data?.url) {
        uploadSuccess = false;
        setIsSuccess(false);
        throw new Error("Invalid response from getPresignedUrl.");
      }

      const { key, url } = resp.data;

      const uploadResp = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": file[0].type, // Set the correct content type
        },
        body: file[0], // Send the file data
      });

      if (!uploadResp.ok) {
        uploadSuccess = false;
        setIsSuccess(false);

        throw new Error(`File upload failed with status: ${uploadResp.status}`);
      }

      if (uploadSuccess) {
        setIsSuccess(true);
        getThumbPathOfImage({
          key,
        }).then((resp) => {
          setImageUrl(resp?.data?.data?.thumbPath?.url);
        });
        const addFilesPayload = {
          image: {
            imagePath: {
              key,
            },
          },
        };
        setFileUpload(addFilesPayload);
        setIsSuccess(true);
        message.success("Image uploaded successfully.");
      } else {
        message.error("Some images failed to upload.");
      }
    } catch (err) {
      console.error(err);
      uploadSuccess = false;
      setIsSuccess(false);
      message.error(
        err?.response?.data?.message ||
          err.message ||
          "An error occurred during upload."
      );
    } finally {
      setIsUploadingImage(false);
    }
    return uploadSuccess;
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/jpg": "",
      "image/png": "",
      "image/jpeg": "",
      "image/webp": "",
    },
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        setImageUrl("");
        setFileUpload({});
        setFormInput((prev) => ({
          ...prev,
          marketPlacelogo: acceptedFiles[0],
        }));
        setIsUploadingImage(true);
        setIsDragging(false);

        getKeyPresignedUrl(acceptedFiles);
      }
    },
    multiple: false,
  });
  const onDropItems = async (files) => {
    if (files.length > 0) {
      setImageUrl("");
      setFileUpload({});
      setFormInput((prev) => ({
        ...prev,
        marketPlacelogo: files[0],
      }));
      setIsUploadingImage(true);
      setIsDragging(false);
      getKeyPresignedUrl(files);
    }
  };

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const renderDragAndDropSection = useCallback(
    () =>
      isDragging && (
        <CustomDropZone
          dropZoneClass={"uploaded_images_row"}
          onDropItems={onDropItems}
          isShowTextOnDrag={true}
          acceptingFormates={{
            "image/jpg": "",
            "image/png": "",
            "image/jpeg": "",
            "image/webp": "",
          }}
          handleDragLeave={handleDragLeave}
          multiple={false}
        />
      ),
    [isDragging, onDropItems, handleDragLeave]
  );

  const getUploadWrapper = () => {
    return (
      <>
        {isSuccess ? (
          <div className="upload_image_wrapper">
            {imageUrl ? (
              <>
                {isImageLoading && (
                  <div className="image-placeholder">Loading...</div>
                )}
                <img
                  src={imageUrl}
                  alt="Uploaded"
                  style={{ display: isImageLoading ? "none" : "block" }}
                  onLoad={handleImageLoad}
                />
              </>
            ) : (
              ""
            )}
            <div
              className="cross-icon"
              onClick={() => {
                setIsSuccess(false);
                setFormInput({
                  marketPlacelogo: "", // Clear uploaded logo
                });
              }}
            ></div>
          </div>
        ) : (
          <div className="upload_wrapper">
            <div className="category_image">
              <img src={uploadImage} alt="" />
              <p>Drag your image</p>
            </div>
            <div className="or_image">
              <img src={dividerImage} alt="" />
            </div>
            <label className="browse" onClick={handleAreaClick}>
              Browse files
            </label>
            <input
              type="file"
              accept="image/png,image/jpg,image/jpeg, image/webp"
              id="file"
              onChange={(e) => handleUploadImage(e.target.files)}
              onClick={(e) => (e.target.value = null)}
              className="upload_more_images_btn_input"
            />
          </div>
        )}
      </>
    );
  };
  const handleUploadImage = async (files) => {
    if (files.length > 0) {
      setImageUrl("");
      setFileUpload({});
      setFormInput((prev) => ({
        ...prev,
        marketPlacelogo: files[0],
      }));
      setIsUploadingImage(true);
      getKeyPresignedUrl(files);
    }
  };

  const handleAreaClick = () => {
    document.getElementById("file")?.click();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormInput((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsSubmitted(true);
      setFormInput({
        marketPlaceName: "",
        marketPlacelogo: "",
        reason: "",
      });
      let payload = {
        name: formInput?.marketPlaceName,
        reason: formInput?.reason,
        ...fileUpload,
      };
      createNewMarketPlace(payload);
      setErrors({});
    } else {
      message.error("Please fix the errors in the form.");
    }
  };
  const handlePaste = async (e) => {
    const items = e.clipboardData.items;
    let foundImage = false;
    console.log(e);
    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      if (item.type.includes("image")) {
        foundImage = true;
        const blob = item.getAsFile();

        if (blob) {
          const file = new File(
            [blob],
            `pasted-image.${blob.type.split("/")[1]}`,
            { type: blob.type }
          );

          setImageUrl("");
          setFileUpload({});
          setFormInput((prev) => ({
            ...prev,
            categoryImage: file,
          }));
          setIsUploadingImage(true);
          getKeyPresignedUrl([file]);
        }
      }
    }

    if (!foundImage) {
      message.error("Pasted content is not an image.");
    }
  };
  return (
    <div
      className="create_marketplace_main_wrapper"
      onDragEnter={handleDragEnter}
    >
      <div className="create_marketplace_content">
        <form onSubmit={handleSubmit}>
          <div className="form_control">
            <label>
              Marketplace Name <sup>*</sup>
            </label>
            <input
              placeholder="Enter Marketplace Name"
              name="marketPlaceName"
              value={formInput.marketPlaceName}
              onChange={handleChange}
            />
            {errors.marketPlaceName && (
              <p className="error_message">{errors.marketPlaceName}</p>
            )}
          </div>
          <div className="form_control">
            <label>
              Logo <sup>*</sup>
            </label>
            <div className="drop_down_wrapper" onPaste={handlePaste}>
              <div className="item_upload_wrapper">
                {isUploadingImage ? <Spin /> : getUploadWrapper()}
                {renderDragAndDropSection()}
              </div>
            </div>
            {errors.marketPlacelogo && (
              <p className="error_message">{errors.marketPlacelogo}</p>
            )}
          </div>
          <div className="form_control">
            <label>
              Reason for category creation <sup>*</sup>
            </label>
            <textarea
              placeholder="Write your reason here..."
              name="reason"
              value={formInput.reason}
              onChange={handleChange}
            />
            {errors.reason && <p className="error_message">{errors.reason}</p>}
          </div>
          <div className="action_wrapper">
            <button
              className="cancelBtn"
              onClick={() => setIsCreateMarketplace(false)}
            >
              {" "}
              Cancel
            </button>
            <Button
              width="264px"
              height="57px"
              text="Submit"
              className="approvalBtn"
              isDisabled={
                !formInput.reason ||
                !formInput.marketPlacelogo ||
                !formInput.marketPlaceName
              }
            />
          </div>
        </form>
      </div>
    </div>
  );
};
export default CreateMarketplace;
